import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import AdminCursor from '../components/AdminCursor';
import { useAuth } from '../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function validatePassword(pw) {
  if (!pw || pw.length < 10) return 'Password must be at least 10 characters.';
  if (!/[A-Za-z]/.test(pw)) return 'Password must contain at least one letter.';
  if (!/[0-9]/.test(pw)) return 'Password must contain at least one number.';
  return null;
}

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [invite, setInvite] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setCheckError(e?.response?.data?.detail || 'Invite is no longer valid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) { setError('Full name is required.'); return; }
    const pwErr = validatePassword(password);
    if (pwErr) { setError(pwErr); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const u = await acceptInvite(token, fullName.trim(), password);
      const home = u.role === 'admin' ? '/admin/dashboard' : '/admin/open-call';
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not accept invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-root min-h-screen bg-[#050505] flex items-center justify-center px-6 py-10">
      <AdminCursor />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <p className="text-xs font-mono tracking-[0.3em] text-[#90E0EF] uppercase">The Relative Gallery</p>
          <p className="text-[10px] font-mono text-[#555] tracking-[0.2em] mt-2">CURATOR OS · ACTIVATE ACCOUNT</p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 text-[#90E0EF] animate-spin" />
          </div>
        )}

        {!loading && checkError && (
          <div className="border border-[#333] bg-[#0a0a0a] p-6 text-center" data-testid="invite-invalid">
            <XCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
            <p className="text-[13px] font-mono text-white">{checkError}</p>
            <button
              onClick={() => navigate('/admin/login')}
              className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-[#90E0EF] hover:text-white transition-colors"
              style={{ cursor: 'pointer' }}
            >
              ← Back to login
            </button>
          </div>
        )}

        {!loading && invite && !checkError && (
          <>
            <div className="flex items-center gap-2 mb-6 text-[11px] font-mono text-[#c8c8c8]">
              <ShieldCheck className="w-4 h-4 text-[#90E0EF]" />
              Invited as <span className="text-[#90E0EF] uppercase tracking-[0.15em]">{invite.role}</span>
              <span className="text-[#555]">·</span>
              <span data-testid="invite-email">{invite.email}</span>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="invite-accept-form">
              <div>
                <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">Full name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
                  data-testid="invite-fullname"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
                  data-testid="invite-password"
                />
                <p className="mt-1.5 text-[9px] font-mono text-[#555]">Min 10 characters, at least one letter and one number.</p>
              </div>
              <div>
                <label className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase block mb-2">Confirm password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full bg-transparent border border-[#333] text-white rounded-none font-mono text-sm h-11 px-3 focus:outline-none focus:border-[#90E0EF]"
                  data-testid="invite-confirm"
                />
              </div>

              {error && <p className="text-red-400 text-xs font-mono" data-testid="invite-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="invite-submit"
                className="w-full h-11 bg-[#90E0EF] text-black font-mono text-xs uppercase tracking-[0.2em] rounded-none hover:bg-[#a8e8f4] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ cursor: 'pointer' }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
